import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Card } from '@/components/ui/card';
import communityService from '@/services/communityService';
import api from '@/services/apiService';
import showToast from '@/components/ui/Toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs';
import { ProfileTab, AkunTab } from '@/components/community/ProfileSettings';
import ImageCropModal from '@/components/common/ImageCropModal';
import PhotoGalleryModal from '@/components/common/PhotoGalleryModal';

// Helper to format date into "Month Year" (e.g., "Mar 2021")
const formatMonthYear = (dateStr?: string): string => {
  if (!dateStr) return 'Mar 2021';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return dateStr || 'Mar 2021';
  }
};

const ProfileSettings: React.FC = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<any>(null);
  const [statsData, setStatsData] = useState<{
    event_count: number;
    mabar_count: number;
    sparing_count: number;
    sparing_winrate: number;
    match_count: number;
    member_count: number;
    all_event_count?: number;
    all_match_count?: number;
  }>({
    event_count: 0,
    mabar_count: 0,
    sparing_count: 0,
    sparing_winrate: 0,
    match_count: 0,
    member_count: 0,
    all_event_count: 0,
    all_match_count: 0,
  });
  const [activeStatSport, setActiveStatSport] = useState<string>('futsal');
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editTab, setEditTab] = useState<'profile' | 'akun'>('profile');

  // Direct Image Upload State & Refs
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState<boolean>(false);
  const [isUploadingCover, setIsUploadingCover] = useState<boolean>(false);

  // Image Cropping Modal State
  const [cropModalOpen, setCropModalOpen] = useState<boolean>(false);
  const [cropType, setCropType] = useState<'logo' | 'cover'>('logo');
  const [selectedCropFile, setSelectedCropFile] = useState<File | null>(null);

  // Gallery Modal & Lightbox State
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(true);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState<boolean>(false);
  const [existingGallery, setExistingGallery] = useState<string[]>([]);
  const [newGalleryFiles, setNewGalleryFiles] = useState<File[]>([]);
  const [previewNewGallery, setPreviewNewGallery] = useState<string[]>([]);
  const [isSavingGallery, setIsSavingGallery] = useState<boolean>(false);
  const [galleryPreviewOpen, setGalleryPreviewOpen] = useState<boolean>(false);
  const [galleryPreviewIndex, setGalleryPreviewIndex] = useState<number>(0);

  // Gallery photos derived from profile assets JSONB
  const rawGallery = profileData?.assets?.gallery || [];
  const galleryPhotos: string[] = (Array.isArray(rawGallery) ? rawGallery : []).filter(
    (url): url is string =>
      typeof url === 'string' &&
      url.trim() !== '' &&
      url !== '[]' &&
      url !== 'null' &&
      url !== 'undefined'
  );

  // Fetch Community Profile & Stats
  const fetchProfileAndStats = async () => {
    try {
      setIsLoadingProfile(true);
      const res = await api.get('/community/profile');
      if (res.data?.data) {
        const comm = res.data.data.community || res.data.data;
        setProfileData(comm);

        // Update local storage values if available
        if (comm.name) localStorage.setItem('communityName', comm.name);
        if (comm.slug) localStorage.setItem('communityNickname', comm.slug);
        if (comm.assets?.logo) localStorage.setItem('communityAvatar', comm.assets.logo);
      }
    } catch (err) {
      console.error('Failed to load community profile data:', err);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    fetchProfileAndStats();
  }, []);

  // Gallery Handlers
  const openGalleryModal = () => {
    setExistingGallery(galleryPhotos);
    setNewGalleryFiles([]);
    setPreviewNewGallery([]);
    setIsGalleryModalOpen(true);
  };

  const handlePhotoClick = (idx: number) => {
    setGalleryPreviewIndex(idx);
    setGalleryPreviewOpen(true);
  };

  const handleSelectNewGalleryFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);

    // Check file size (max 2MB per file)
    const oversized = files.find((f) => f.size > 2 * 1024 * 1024);
    if (oversized) {
      showToast.error(`Ukuran file "${oversized.name}" melebihi batas 2MB`);
      return;
    }

    if (existingGallery.length + newGalleryFiles.length + files.length > 6) {
      showToast.error('Maksimal 6 foto di galeri');
      return;
    }
    setNewGalleryFiles((prev) => [...prev, ...files]);

    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviewNewGallery((prev) => [...prev, ...previews]);
  };

  const handleRemoveExistingPhoto = (index: number) => {
    setExistingGallery((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleRemoveNewPhoto = (index: number) => {
    setNewGalleryFiles((prev) => prev.filter((_, idx) => idx !== index));
    setPreviewNewGallery((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveGallery = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSavingGallery(true);
      const payload = {
        name: profileData?.name || localStorage.getItem('communityName') || '',
        sport_category: profileData?.sport_category || 'Futsal',
        location: profileData?.location || 'Yogyakarta',
        slug: profileData?.slug || undefined,
        description: profileData?.description || '',
        is_public: profileData?.is_public ?? true,
        keep_assets: {
          logo: profileData?.assets?.logo || undefined,
          cover: profileData?.assets?.cover || undefined,
          gallery: existingGallery,
        },
        gallery: newGalleryFiles.length > 0 ? newGalleryFiles : undefined,
      };

      await communityService.updateProfile(payload);
      showToast.success('Galeri berhasil diperbarui!');
      setIsGalleryModalOpen(false);
      await fetchProfileAndStats();
    } catch (err: any) {
      console.error('Failed to save gallery:', err);
      const errMsg = err?.response?.data?.message || err?.message || 'Gagal menyimpan galeri';
      showToast.error(errMsg);
    } finally {
      setIsSavingGallery(false);
    }
  };

  // Fetch Sport Specific Stats
  useEffect(() => {
    const fetchSportStats = async () => {
      try {
        const res = await api.get(`/community/stats?sport=${activeStatSport}`);
        if (res.data?.data) {
          setStatsData(res.data.data);
        }
      } catch (err) {
        console.warn('Failed to load sport stats:', err);
      }
    };

    fetchSportStats();
  }, [activeStatSport]);

  // Direct Logo File Select Handler -> Open Crop Modal
  const handleLogoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showToast.error('Ukuran file maksimal 2MB');
      if (logoInputRef.current) logoInputRef.current.value = '';
      return;
    }

    setSelectedCropFile(file);
    setCropType('logo');
    setCropModalOpen(true);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  // Direct Cover File Select Handler -> Open Crop Modal
  const handleCoverFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showToast.error('Ukuran file maksimal 2MB');
      if (coverInputRef.current) coverInputRef.current.value = '';
      return;
    }

    setSelectedCropFile(file);
    setCropType('cover');
    setCropModalOpen(true);
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  // Handle Crop Completed & Upload
  const handleCropComplete = async (croppedFile: File) => {
    if (cropType === 'logo') {
      setIsUploadingLogo(true);
      try {
        const payload = {
          name: profileData?.name || localStorage.getItem('communityName') || '',
          sport_category: profileData?.sport_category || 'Futsal',
          location: profileData?.location || 'Yogyakarta',
          slug: profileData?.slug || undefined,
          description: profileData?.description || '',
          is_public: profileData?.is_public ?? true,
          logo: croppedFile,
          keep_assets: {
            cover: profileData?.assets?.cover_image || profileData?.assets?.cover || undefined,
            gallery: profileData?.assets?.gallery || [],
          },
        };

        await communityService.updateProfile(payload);
        showToast.success('Logo komunitas berhasil diperbarui!');
        await fetchProfileAndStats();
      } catch (err: any) {
        console.error('Failed to upload logo:', err);
        showToast.error(err?.response?.data?.message || 'Gagal memperbarui logo komunitas');
      } finally {
        setIsUploadingLogo(false);
      }
    } else {
      setIsUploadingCover(true);
      try {
        const payload = {
          name: profileData?.name || localStorage.getItem('communityName') || '',
          sport_category: profileData?.sport_category || 'Futsal',
          location: profileData?.location || 'Yogyakarta',
          slug: profileData?.slug || undefined,
          description: profileData?.description || '',
          is_public: profileData?.is_public ?? true,
          cover: croppedFile,
          keep_assets: {
            logo: profileData?.assets?.logo || undefined,
            gallery: profileData?.assets?.gallery || [],
          },
        };

        await communityService.updateProfile(payload);
        showToast.success('Sampul komunitas berhasil diperbarui!');
        await fetchProfileAndStats();
      } catch (err: any) {
        console.error('Failed to upload cover:', err);
        showToast.error(err?.response?.data?.message || 'Gagal memperbarui sampul komunitas');
      } finally {
        setIsUploadingCover(false);
      }
    }
  };

  const communityName = profileData?.name || profileData?.community_user?.name || localStorage.getItem('communityName') || 'Komunitas';
  const communitySlug = profileData?.slug || localStorage.getItem('communityNickname') || 'komunitas';
  const communityLocation = profileData?.location || '-';
  const sportCategory = profileData?.sport_category_r?.name || profileData?.sport_category || '-';
  const logoUrl = profileData?.assets?.logo || profileData?.community_user?.avatar || localStorage.getItem('communityAvatar') || '';
  const coverUrl = profileData?.assets?.cover_image || profileData?.assets?.cover || '';
  const isVerified = profileData?.community_user?.is_verified ?? false;
  const foundedDate = profileData?.information?.founded_date ? formatMonthYear(profileData.information.founded_date) : (profileData?.created_at ? formatMonthYear(profileData.created_at) : '-');
  const schedule = profileData?.information?.regular_schedule || profileData?.information?.schedule || '-';
  const homeVenue = profileData?.information?.home_venue || profileData?.location || '-';

  const initials = communityName
    ? communityName
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : 'GM';

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-0 sm:p-4 md:py-6 text-[#0E1726]">
      {/* Hidden File Inputs for Direct Upload */}
      <input
        type="file"
        ref={logoInputRef}
        accept="image/*"
        className="hidden"
        onChange={handleLogoFileSelect}
      />
      <input
        type="file"
        ref={coverInputRef}
        accept="image/*"
        className="hidden"
        onChange={handleCoverFileSelect}
      />

      {/* ========================================================================= */}
      {/* 📱 MOBILE VIEW (< md) */}
      {/* ========================================================================= */}
      <main className="w-full max-w-md min-h-screen sm:min-h-0 sm:rounded-3xl overflow-hidden px-4 space-y-5 pt-12 pb-6 md:hidden">
        
        {/* 📷 COVER IMAGE & PROFILE HEADER CARD */}
        <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-2xs overflow-hidden">
          {/* COVER IMAGE BAR */}
          <div className="relative h-48 w-full bg-gradient-to-r from-slate-700 via-gray-800 to-slate-900 overflow-hidden">
            {coverUrl ? (
              <img
                src={coverUrl}
                alt="Cover Sampul"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-800/80 text-white/40">
                <Icon icon="ph:image-square-bold" className="w-12 h-12" />
              </div>
            )}

            {/* Top Bar Action Buttons on Cover */}
            <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-10">
              <button
                type="button"
                disabled={isUploadingCover}
                onClick={() => coverInputRef.current?.click()}
                className="w-auto px-3.5 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center gap-1.5 text-white text-xs font-bold hover:bg-white/30 transition-all cursor-pointer shadow-xs active:scale-95 disabled:opacity-50"
              >
                {isUploadingCover ? (
                  <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-white" />
                ) : (
                  <Icon icon="ph:camera-bold" className="w-4 h-4 text-white" />
                )}
                <span>{isUploadingCover ? 'Mengunggah...' : 'Sampul'}</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/host/menu')}
                className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-white hover:bg-white/30 transition-all cursor-pointer shadow-xs active:scale-95"
                aria-label="Pengaturan Menu"
              >
                <Icon icon="ph:list-bold" className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* MAIN ORANGE CARD BACKGROUND (#FC480D) */}
          <div className="bg-[#FC480D] pt-14 pb-6 px-4 text-center text-white relative rounded-b-3xl">
            
            {/* OVERLAPPING AVATAR */}
            <div className="absolute -top-14 left-1/2 -translate-x-1/2 z-20">
              <div className="relative">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={communityName}
                    className="w-28 h-28 rounded-full object-cover border-4 border-[#FC480D] shadow-lg bg-white"
                  />
                ) : (
                  <div className="w-28 h-28 rounded-full border-4 border-[#FC480D] bg-orange-600 text-white font-black text-3xl flex items-center justify-center shadow-lg">
                    {initials}
                  </div>
                )}

                {/* Edit Icon Button on Avatar */}
                <button
                  type="button"
                  disabled={isUploadingLogo}
                  onClick={() => logoInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-white text-[#FC480D] shadow-md flex items-center justify-center border border-gray-200 hover:bg-gray-50 active:scale-95 transition-all cursor-pointer disabled:opacity-50"
                  aria-label="Edit Logo"
                >
                  {isUploadingLogo ? (
                    <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-[#FC480D]" />
                  ) : (
                    <Icon icon="ph:pencil-simple-bold" className="w-4 h-4 text-[#FC480D]" />
                  )}
                </button>
              </div>
            </div>

            {/* Community Name & Verified Badge */}
            <div className="mt-2 flex items-center justify-center gap-1.5">
              <h1 className="text-2xl font-black text-white tracking-tight">
                {communityName}
              </h1>
              {isVerified && <Icon icon="ph:check-circle-fill" className="w-6 h-6 text-white" />}
            </div>

            {/* Handle / Username Pill */}
            <div className="mt-1.5 inline-flex items-center gap-1 px-3.5 py-1 rounded-full bg-white/20 backdrop-blur-xs text-white text-xs font-semibold">
              <span>@{communitySlug}</span>
            </div>

            {/* Sport Category & Location Pills */}
            <div className="mt-3 flex items-center justify-center flex-wrap gap-2">
              <div className="px-3.5 py-1.5 rounded-full bg-white/20 backdrop-blur-xs text-white text-xs font-semibold">
                {sportCategory}
              </div>
              <div className="px-3.5 py-1.5 rounded-full bg-white/20 backdrop-blur-xs text-white text-xs font-semibold flex items-center gap-1">
                <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5" />
                <span>{communityLocation}</span>
              </div>
            </div>

            {/* 3 STAT CARDS INSIDE ORANGE CARD */}
            <div className="grid grid-cols-3 gap-2.5 mt-5">
              <div className="bg-white/20 backdrop-blur-md rounded-2xl p-3.5 text-center text-white border border-white/20 shadow-xs flex flex-col items-center justify-center">
                <Icon icon="ph:calendar-dots-bold" className="w-5 h-5 opacity-90 mb-1" />
                <span className="text-xl font-black leading-tight">
                  {statsData.all_event_count ?? statsData.event_count ?? 0}
                </span>
                <span className="text-[11px] font-semibold opacity-90">Event</span>
              </div>

              <div className="bg-white/20 backdrop-blur-md rounded-2xl p-3.5 text-center text-white border border-white/20 shadow-xs flex flex-col items-center justify-center">
                <Icon icon="ph:soccer-ball-bold" className="w-5 h-5 opacity-90 mb-1" />
                <span className="text-xl font-black leading-tight">
                  {statsData.all_match_count ?? statsData.match_count ?? 0}
                </span>
                <span className="text-[11px] font-semibold opacity-90">Pertandingan</span>
              </div>

              <div className="bg-white/20 backdrop-blur-md rounded-2xl p-3.5 text-center text-white border border-white/20 shadow-xs flex flex-col items-center justify-center">
                <Icon icon="ph:users-three-bold" className="w-5 h-5 opacity-90 mb-1" />
                <span className="text-xl font-black leading-tight">
                  {statsData.member_count ?? 0}
                </span>
                <span className="text-[11px] font-semibold opacity-90">Member</span>
              </div>
            </div>
          </div>
        </div>

        {/* ℹ️ CARD INFO KOMUNITAS */}
        <Card className="bg-white p-5 rounded-3xl border border-[#E0E6ED] shadow-2xs">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-extrabold text-[#0E1726]">Info komunitas</h2>
            <button
              type="button"
              onClick={() => navigate('/host/profile-settings/edit-info')}
              className="px-3 py-1 rounded-full border border-orange-200 text-[#FC480D] bg-orange-50 text-xs font-bold flex items-center gap-1 hover:bg-orange-100 transition-all cursor-pointer"
            >
              <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-[#F8FAFC] p-3.5 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <Icon icon="ph:users-three-bold" className="w-4 h-4 text-[#FC480D]" />
                <span>Anggota</span>
              </div>
              <p className="font-extrabold text-[#0E1726] text-sm mt-1">
                {statsData.member_count ?? 0} orang
              </p>
            </div>

            <div className="bg-[#F8FAFC] p-3.5 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <Icon icon="ph:flag-bold" className="w-4 h-4 text-[#FC480D]" />
                <span>Berdiri</span>
              </div>
              <p className="font-extrabold text-[#0E1726] text-sm mt-1">{foundedDate}</p>
            </div>

            <div className="bg-[#F8FAFC] p-3.5 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <Icon icon="ph:calendar-bold" className="w-4 h-4 text-[#FC480D]" />
                <span>Jadwal rutin</span>
              </div>
              <p className="font-extrabold text-[#0E1726] text-sm mt-1">{schedule}</p>
            </div>

            <div className="bg-[#F8FAFC] p-3.5 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <Icon icon="ph:map-pin-bold" className="w-4 h-4 text-[#FC480D]" />
                <span>Home venue</span>
              </div>
              <p className="font-extrabold text-[#0E1726] text-sm mt-1 truncate">{homeVenue}</p>
            </div>
          </div>
        </Card>

        {/* 📊 CARD RINGKASAN STATISTIK */}
        <Card className="bg-white p-5 rounded-3xl border border-[#E0E6ED] shadow-2xs">
          <h2 className="text-base font-extrabold text-[#0E1726]">Ringkasan statistik</h2>

          {/* Sport Selector Pills */}
          <div className=" p-1 rounded-2xl flex items-center gap-1 mt-3.5">
            {['futsal', 'mini-soccer', 'sepak-bola'].map((sport) => {
              const label =
                sport === 'futsal'
                  ? 'Futsal'
                  : sport === 'mini-soccer'
                  ? 'Mini soccer'
                  : 'Sepak bola';
              const active = activeStatSport === sport;

              return (
                <button
                  key={sport}
                  onClick={() => setActiveStatSport(sport)}
                  className={`flex-1 py-1.5 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    active
                      ? 'bg-white text-[#0E1726] shadow-xs'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-white p-3.5 rounded-2xl border border-[#E0E6ED] shadow-2xs flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center flex-shrink-0">
                <Icon icon="ph:calendar-bold" className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <span className="text-lg font-black text-[#0E1726] block leading-tight">
                  {statsData.event_count ?? 0}
                </span>
                <span className="text-xs text-gray-400 font-semibold block whitespace-nowrap">Total event</span>
              </div>
            </div>

            <div className="bg-white p-3.5 rounded-2xl border border-[#E0E6ED] shadow-2xs flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center flex-shrink-0">
                <Icon icon="ph:users-bold" className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <span className="text-lg font-black text-[#0E1726] block leading-tight">
                  {statsData.mabar_count ?? 0}
                </span>
                <span className="text-xs text-gray-400 font-semibold block whitespace-nowrap">Event mabar</span>
              </div>
            </div>

            <div className="bg-white p-3.5 rounded-2xl border border-[#E0E6ED] shadow-2xs flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center flex-shrink-0">
                <Icon icon="ph:shield-bold" className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <span className="text-lg font-black text-[#0E1726] block leading-tight">
                  {statsData.sparing_count ?? 0}
                </span>
                <span className="text-xs text-gray-400 font-semibold block whitespace-nowrap">Total sparing</span>
              </div>
            </div>

            <div className="bg-white p-3.5 rounded-2xl border border-[#E0E6ED] shadow-2xs flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center flex-shrink-0">
                <Icon icon="ph:trophy-bold" className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <span className="text-lg font-black text-[#0E1726] block leading-tight">
                  {Math.round(statsData.sparing_winrate ?? 0)}%
                </span>
                <span className="text-xs text-gray-400 font-semibold block whitespace-nowrap">Winrate sparing</span>
              </div>
            </div>
          </div>
        </Card>
        
        {/* 🖼️ CARD GALERI */}
        {isLoadingProfile ? (
          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="h-5 w-20 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-8 w-20 bg-gray-200 rounded-full animate-pulse" />
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-square rounded-2xl bg-gray-200 animate-pulse" />
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#0E1726]">Galeri</h3>
              <button
                type="button"
                onClick={openGalleryModal}
                className="h-8 px-3.5 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Icon icon="ph:sliders-horizontal" className="w-3.5 h-3.5" />
                <span>Manage</span>
              </button>
            </div>

            {galleryPhotos.length > 0 ? (
              <div className="grid grid-cols-3 gap-2.5">
                {galleryPhotos.map((photo, idx) => (
                  <div
                    key={idx}
                    onClick={() => handlePhotoClick(idx)}
                    className="aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 hover:opacity-90 transition-opacity cursor-pointer"
                  >
                    <img src={photo} alt={`Galeri ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 px-4 rounded-2xl border-2 border-dashed border-gray-200 text-center flex flex-col items-center justify-center space-y-3 bg-gray-50/50">
                <div className="w-14 h-14 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center">
                  <Icon icon="ph:image-square-bold" className="w-7 h-7" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-[#0E1726]">Belum ada foto galeri</h4>
                  <p className="text-xs text-gray-400 max-w-xs">
                    Tunjukkan momen terbaik komunitasmu di lapangan dengan menambahkan foto ke galeri profil.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openGalleryModal}
                  className="h-9 px-4 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs mt-1"
                >
                  <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
                  <span>Tambah foto</span>
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ========================================================================= */}
      {/* 💻 DESKTOP VIEW (>= md) */}
      {/* ========================================================================= */}
      <main className="hidden md:block w-full max-w-6xl mx-auto space-y-6 pb-12">
        {/* DESKTOP HERO HEADER CARD */}
        <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-sm overflow-hidden">
          {/* COVER IMAGE */}
          <div className="relative h-48 sm:h-56 min-[1080px]:h-64 w-full bg-gradient-to-r from-slate-700 via-gray-800 to-slate-900 overflow-hidden">
            {coverUrl ? (
              <img src={coverUrl} alt="Cover Sampul" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-800/80 text-white/40">
                <Icon icon="ph:image-square-bold" className="w-16 h-16" />
              </div>
            )}

            {/* Top Bar Action Buttons */}
            <div className="absolute top-4 right-4 flex items-center gap-2 z-10 flex-wrap justify-end">
              <button
                type="button"
                disabled={isUploadingCover}
                onClick={() => coverInputRef.current?.click()}
                className="px-3.5 sm:px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center gap-1.5 sm:gap-2 text-white text-xs font-bold hover:bg-white/30 transition-all cursor-pointer shadow-xs active:scale-95 disabled:opacity-50"
              >
                {isUploadingCover ? (
                  <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-white" />
                ) : (
                  <Icon icon="ph:camera-bold" className="w-4 h-4 text-white" />
                )}
                <span>{isUploadingCover ? 'Mengunggah...' : 'Ubah Sampul'}</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/host/menu')}
                className="px-3.5 sm:px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center gap-1.5 sm:gap-2 text-white text-xs font-bold hover:bg-white/30 transition-all cursor-pointer shadow-xs active:scale-95"
              >
                <Icon icon="ph:gear-bold" className="w-4 h-4 text-white" />
                <span>Pengaturan Menu</span>
              </button>
            </div>
          </div>

          {/* MAIN ORANGE CARD BACKGROUND */}
          <div className="bg-[#FC480D] pt-14 sm:pt-16 pb-6 sm:pb-8 px-5 sm:px-8 text-white relative rounded-b-3xl">
            {/* OVERLAPPING AVATAR */}
            <div className="absolute -top-12 sm:-top-14 min-[1080px]:-top-16 left-5 sm:left-8 z-20">
              <div className="relative">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={communityName}
                    className="w-24 h-24 sm:w-28 sm:h-28 min-[1080px]:w-32 min-[1080px]:h-32 rounded-full object-cover border-4 border-[#FC480D] shadow-xl bg-white"
                  />
                ) : (
                  <div className="w-24 h-24 sm:w-28 sm:h-28 min-[1080px]:w-32 min-[1080px]:h-32 rounded-full border-4 border-[#FC480D] bg-orange-600 text-white font-black text-2xl sm:text-3xl min-[1080px]:text-4xl flex items-center justify-center shadow-xl">
                    {initials}
                  </div>
                )}

                <button
                  type="button"
                  disabled={isUploadingLogo}
                  onClick={() => logoInputRef.current?.click()}
                  className="absolute bottom-0 sm:bottom-1 right-0 sm:right-1 w-7 h-7 sm:w-8 sm:h-8 min-[1080px]:w-9 min-[1080px]:h-9 rounded-full bg-white text-[#FC480D] shadow-md flex items-center justify-center border border-gray-200 hover:bg-gray-50 active:scale-95 transition-all cursor-pointer disabled:opacity-50"
                  aria-label="Edit Logo"
                >
                  {isUploadingLogo ? (
                    <Icon icon="ph:spinner-gap-bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin text-[#FC480D]" />
                  ) : (
                    <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#FC480D]" />
                  )}
                </button>
              </div>
            </div>

            {/* Desktop Hero Info & Stats Flex Bar */}
            <div className="flex flex-col min-[1080px]:flex-row min-[1080px]:items-end justify-between gap-5 min-[1080px]:gap-6 min-[1080px]:pl-40">
              <div className="space-y-2 min-w-0 pl-28 sm:pl-32 min-[1080px]:pl-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl sm:text-2xl min-[1080px]:text-3xl font-black tracking-tight">{communityName}</h1>
                  {isVerified && <Icon icon="ph:check-circle-fill" className="w-6 h-6 min-[1080px]:w-7 min-[1080px]:h-7 text-white shrink-0" />}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-3 sm:px-3.5 py-1 rounded-full bg-white/20 backdrop-blur-xs text-xs font-semibold">
                    @{communitySlug}
                  </span>
                  <span className="px-3 sm:px-3.5 py-1 rounded-full bg-white/20 backdrop-blur-xs text-xs font-semibold">
                    {sportCategory}
                  </span>
                  <span className="px-3 sm:px-3.5 py-1 rounded-full bg-white/20 backdrop-blur-xs text-xs font-semibold flex items-center gap-1">
                    <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5" />
                    <span>{communityLocation}</span>
                  </span>
                </div>
              </div>

              {/* Top 3 Quick Stats Badges */}
              <div className="grid grid-cols-3 gap-2.5 sm:gap-3 w-full min-[1080px]:w-auto min-[1080px]:flex min-[1080px]:items-center min-[1080px]:gap-3 min-[1080px]:shrink-0 pt-2 min-[1080px]:pt-0">
                <div className="bg-white/20 backdrop-blur-md rounded-2xl px-3 sm:px-5 py-3 text-center border border-white/20 shadow-xs min-[1080px]:min-w-[100px]">
                  <span className="text-xl sm:text-2xl font-black block leading-tight truncate">
                    {statsData.event_count ?? 0}
                  </span>
                  <span className="text-[11px] sm:text-xs font-semibold opacity-90 truncate block">Event</span>
                </div>
                <div className="bg-white/20 backdrop-blur-md rounded-2xl px-3 sm:px-5 py-3 text-center border border-white/20 shadow-xs min-[1080px]:min-w-[110px]">
                  <span className="text-xl sm:text-2xl font-black block leading-tight truncate">
                    {statsData.match_count ?? 0}
                  </span>
                  <span className="text-[11px] sm:text-xs font-semibold opacity-90 truncate block">Pertandingan</span>
                </div>
                <div className="bg-white/20 backdrop-blur-md rounded-2xl px-3 sm:px-5 py-3 text-center border border-white/20 shadow-xs min-[1080px]:min-w-[100px]">
                  <span className="text-xl sm:text-2xl font-black block leading-tight truncate">
                    {statsData.member_count ?? 0}
                  </span>
                  <span className="text-[11px] sm:text-xs font-semibold opacity-90 truncate block">Member</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* DESKTOP 2-COLUMN DASHBOARD GRID */}
        <div className="grid grid-cols-12 gap-6 items-start">
          {/* LEFT COLUMN: Info Komunitas (4 cols) */}
          <div className="col-span-12 lg:col-span-4 space-y-6">
            <Card className="bg-white p-6 rounded-3xl border border-[#E0E6ED] shadow-2xs space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-extrabold text-[#0E1726]">Info komunitas</h2>
                <button
                  type="button"
                  onClick={() => navigate('/host/profile-settings/edit-info')}
                  className="px-3.5 py-1.5 rounded-full border border-orange-200 text-[#FC480D] bg-orange-50 text-xs font-bold flex items-center gap-1.5 hover:bg-orange-100 transition-all cursor-pointer"
                >
                  <Icon icon="ph:pencil-simple-bold" className="w-4 h-4" />
                  <span>Edit Info</span>
                </button>
              </div>

              <div className="space-y-3 pt-1">
                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                    <Icon icon="ph:users-three-bold" className="w-4.5 h-4.5 text-[#FC480D]" />
                    <span>Anggota</span>
                  </div>
                  <p className="font-extrabold text-[#0E1726] text-sm">
                    {statsData.member_count ?? 0} orang
                  </p>
                </div>

                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                    <Icon icon="ph:flag-bold" className="w-4.5 h-4.5 text-[#FC480D]" />
                    <span>Berdiri</span>
                  </div>
                  <p className="font-extrabold text-[#0E1726] text-sm">{foundedDate}</p>
                </div>

                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                    <Icon icon="ph:calendar-bold" className="w-4.5 h-4.5 text-[#FC480D]" />
                    <span>Jadwal rutin</span>
                  </div>
                  <p className="font-extrabold text-[#0E1726] text-sm">{schedule}</p>
                </div>

                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                    <Icon icon="ph:map-pin-bold" className="w-4.5 h-4.5 text-[#FC480D]" />
                    <span>Home venue</span>
                  </div>
                  <p className="font-extrabold text-[#0E1726] text-sm truncate max-w-[160px]">
                    {homeVenue}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* RIGHT COLUMN: Ringkasan Statistik & Galeri (8 cols) */}
          <div className="col-span-12 lg:col-span-8 space-y-6">
            {/* RINGKASAN STATISTIK */}
            <Card className="bg-white p-6 rounded-3xl border border-[#E0E6ED] shadow-2xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h2 className="text-lg font-extrabold text-[#0E1726]">Ringkasan statistik</h2>

                {/* Sport Selector Pills */}
                <div className=" p-1 rounded-2xl flex items-center gap-1 w-full sm:w-auto">
                  {['futsal', 'mini-soccer', 'sepak-bola'].map((sport) => {
                    const label =
                      sport === 'futsal'
                        ? 'Futsal'
                        : sport === 'mini-soccer'
                        ? 'Mini soccer'
                        : 'Sepak bola';
                    const active = activeStatSport === sport;

                    return (
                      <button
                        key={sport}
                        onClick={() => setActiveStatSport(sport)}
                        className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                          active
                            ? 'bg-white text-[#0E1726] shadow-xs'
                            : 'text-gray-500 hover:text-gray-800'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-1">
                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 flex flex-col justify-between space-y-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                    <Icon icon="ph:calendar-bold" className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-2xl font-black text-[#0E1726] block leading-tight">
                      {statsData.event_count ?? 0}
                    </span>
                    <span className="text-xs text-gray-400 font-semibold block mt-0.5">Total event</span>
                  </div>
                </div>

                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 flex flex-col justify-between space-y-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                    <Icon icon="ph:users-bold" className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-2xl font-black text-[#0E1726] block leading-tight">
                      {statsData.mabar_count ?? 0}
                    </span>
                    <span className="text-xs text-gray-400 font-semibold block mt-0.5">Event mabar</span>
                  </div>
                </div>

                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 flex flex-col justify-between space-y-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                    <Icon icon="ph:shield-bold" className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-2xl font-black text-[#0E1726] block leading-tight">
                      {statsData.sparing_count ?? 0}
                    </span>
                    <span className="text-xs text-gray-400 font-semibold block mt-0.5">Total sparing</span>
                  </div>
                </div>

                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 flex flex-col justify-between space-y-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                    <Icon icon="ph:trophy-bold" className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-2xl font-black text-[#0E1726] block leading-tight">
                      {Math.round(statsData.sparing_winrate ?? 0)}%
                    </span>
                    <span className="text-xs text-gray-400 font-semibold block mt-0.5">Winrate sparing</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* GALERI */}
            {isLoadingProfile ? (
              <div className="bg-white rounded-3xl p-6 border border-[#E0E6ED] shadow-2xs space-y-4">
                <div className="flex items-center justify-between">
                  <div className="h-6 w-24 bg-gray-200 rounded-lg animate-pulse" />
                  <div className="h-9 w-24 bg-gray-200 rounded-full animate-pulse" />
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="aspect-square rounded-2xl bg-gray-200 animate-pulse" />
                  ))}
                </div>
              </div>
            ) : (
              <Card className="bg-white p-6 rounded-3xl border border-[#E0E6ED] shadow-2xs space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-extrabold text-[#0E1726]">Galeri foto</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Momen terbaik kegiatan komunitas di lapangan</p>
                  </div>
                  <button
                    type="button"
                    onClick={openGalleryModal}
                    className="h-9 px-4 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer"
                  >
                    <Icon icon="ph:sliders-horizontal" className="w-4 h-4" />
                    <span>Kelola Galeri</span>
                  </button>
                </div>

                {galleryPhotos.length > 0 ? (
                  <div className="grid grid-cols-4 gap-3 pt-2">
                    {galleryPhotos.map((photo, idx) => (
                      <div
                        key={idx}
                        onClick={() => handlePhotoClick(idx)}
                        className="aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 hover:opacity-90 transition-all hover:scale-[1.02] cursor-pointer shadow-2xs"
                      >
                        <img src={photo} alt={`Galeri ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 px-4 rounded-2xl border-2 border-dashed border-gray-200 text-center flex flex-col items-center justify-center space-y-3 bg-gray-50/50">
                    <div className="w-16 h-16 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center">
                      <Icon icon="ph:image-square-bold" className="w-8 h-8" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-sm font-bold text-[#0E1726]">Belum ada foto galeri</h4>
                      <p className="text-xs text-gray-400 max-w-sm">
                        Tunjukkan momen terbaik komunitasmu di lapangan dengan menambahkan foto ke galeri profil.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openGalleryModal}
                      className="h-10 px-5 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer shadow-xs mt-2"
                    >
                      <Icon icon="ph:plus-bold" className="w-4 h-4" />
                      <span>Tambah foto galeri</span>
                    </button>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* ========================================================================= */}
      {/* 🖼️ MANAGE GALLERY MODAL */}
      {/* ========================================================================= */}
      {/* ========================================================================= */}
      {isGalleryModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-in slide-in-from-bottom duration-300">
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#E0E6ED] flex items-center justify-between bg-white sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsGalleryModalOpen(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-[#0E1726] hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
                </button>
                <h2 className="text-base font-bold text-[#0E1726]">Kelola Galeri Foto</h2>
              </div>
              <span className="text-xs text-gray-400 font-medium">
                {existingGallery.length + newGalleryFiles.length}/6 Foto
              </span>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSaveGallery} className="p-5 overflow-y-auto space-y-5 flex-1">
              {/* Existing Photos Grid */}
              {existingGallery.length > 0 && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-500 block">Foto Tersimpan</label>
                  <div className="grid grid-cols-3 gap-2.5">
                    {existingGallery.map((url, idx) => (
                      <div key={idx} className="relative aspect-square rounded-2xl overflow-hidden group border border-gray-200">
                        <img src={url} alt={`Saved ${idx}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => handleRemoveExistingPhoto(idx)}
                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:scale-110 transition-transform cursor-pointer"
                        >
                          <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Preview Photos Grid */}
              {previewNewGallery.length > 0 && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-500 block">Foto Baru (Belum Disimpan)</label>
                  <div className="grid grid-cols-3 gap-2.5">
                    {previewNewGallery.map((url, idx) => (
                      <div key={idx} className="relative aspect-square rounded-2xl overflow-hidden group border border-amber-300">
                        <img src={url} alt={`New ${idx}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => handleRemoveNewPhoto(idx)}
                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:scale-110 transition-transform cursor-pointer"
                        >
                          <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Photos Upload Dropzone */}
              {existingGallery.length + newGalleryFiles.length < 6 && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-500 block">Upload Foto Baru</label>
                  <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 hover:border-[#FC480D] rounded-2xl cursor-pointer bg-gray-50 hover:bg-orange-50/50 transition-colors">
                    <Icon icon="ph:cloud-arrow-up-bold" className="w-8 h-8 text-[#FC480D] mb-1" />
                    <span className="text-xs font-bold text-[#0E1726]">Klik untuk pilih foto</span>
                    <span className="text-[11px] text-gray-400 mt-0.5">Format JPG/PNG/WebP, Max 2MB per foto</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleSelectNewGalleryFiles}
                      className="hidden"
                    />
                  </label>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isSavingGallery}
                  className="w-full h-12 rounded-full bg-[#FC480D] hover:bg-[#D83B08] disabled:opacity-50 text-white font-bold text-sm shadow-md transition-all active:scale-98 cursor-pointer flex items-center justify-center gap-2"
                >
                  {isSavingGallery ? (
                    <>
                      <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                      <span>Menyimpan...</span>
                    </>
                  ) : (
                    <span>Simpan galeri</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 🔍 PHOTO GALLERY LIGHTBOX MODAL */}
      {/* ========================================================================= */}
      <PhotoGalleryModal
        open={galleryPreviewOpen}
        onOpenChange={setGalleryPreviewOpen}
        photos={galleryPhotos}
        initialIndex={galleryPreviewIndex}
        title={`Galeri ${profileData?.name || 'Komunitas'}`}
      />

      {/* ========================================================================= */}
      {/* ✂️ IMAGE CROPPER MODAL */}
      {/* ========================================================================= */}
      <ImageCropModal
        isOpen={cropModalOpen}
        onClose={() => {
          setCropModalOpen(false);
          setSelectedCropFile(null);
        }}
        imageFile={selectedCropFile}
        type={cropType}
        onCropComplete={handleCropComplete}
      />

      {/* ========================================================================= */}
      {/* ✏️ EDIT PROFILE & AKUN MODAL DIALOG */}
      {/* ========================================================================= */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl p-6 bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-[#0E1726] flex items-center gap-2">
              <Icon icon="ph:gear-bold" className="w-5 h-5 text-[#FC480D]" />
              Pengaturan Profil Komunitas
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue={editTab} onValueChange={(val) => setEditTab(val as 'profile' | 'akun')} className="w-full mt-2">
            <TabsList className="w-full grid grid-cols-2  p-1 rounded-2xl">
              <TabsTrigger value="profile" className="rounded-xl font-bold text-xs py-2">
                Profil
              </TabsTrigger>
              <TabsTrigger value="akun" className="rounded-xl font-bold text-xs py-2">
                Akun
              </TabsTrigger>
            </TabsList>
            <TabsContent value="profile" className="mt-4">
              <ProfileTab />
            </TabsContent>
            <TabsContent value="akun" className="mt-4">
              <AkunTab />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfileSettings;
